import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { Alert, Button, Modal, Space, Spin, Table, Typography, message } from 'antd';
import { API } from '@/services/api.service';
import { errorText, formatAmount } from '../format';

const { Text } = Typography;

/**
 * An income statement file already sent to the parent, set against the
 * ledger. It becomes the company's form, its map is taken over and its months
 * are frozen, only when the accountant says so.
 *
 * @param {Object} props - Component props
 * @param {string} props.companyId - Company
 * @param {File} props.file - The .xlsx
 * @param {number} props.year - Year the file is for
 * @param {Function} props.onClose - Close without taking anything over
 * @param {Function} props.onApplied - Called after the file is taken over
 * @returns {JSX.Element} The dialog
 */
export default function ParentPlImportModal({ companyId, file, year, onClose, onApplied }) {
  const { t } = useTranslation('clientReports');

  const previewMutation = useMutation({
    mutationFn: () => API.clientReports.importParentIncomeStatement(companyId, file, { year, apply: false }),
    onError: async (e) => {
      message.error(await errorText(e));
      onClose();
    },
  });
  const applyMutation = useMutation({
    mutationFn: () => API.clientReports.importParentIncomeStatement(companyId, file, { year, apply: true }),
    onSuccess: () => {
      message.success(t('parentPl.importApplied'));
      onApplied();
    },
    onError: async (e) => message.error(await errorText(e)),
  });

  useEffect(() => {
    previewMutation.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  const result = previewMutation.data?.data;
  const labelOf = new Map((result?.lines || []).map((l) => [l.key, l.label]));
  const differences = (result?.months || []).flatMap((mo) =>
    mo.differences.map((key) => ({
      key: `${mo.month}-${key}`,
      month: mo.month,
      line: labelOf.get(key) || key,
      inFile: mo.values[key] || 0,
      ledger: mo.computed[key] || 0,
    }))
  );
  const ledgerCount = (result?.lines || []).filter((l) => l.kind === 'ledger').length;
  const manual = (result?.lines || []).filter((l) => l.kind === 'manual');

  return (
    <Modal
      open
      width={900}
      title={t('parentPl.importTitle')}
      onCancel={onClose}
      footer={
        <Space>
          <Button shape="round" onClick={onClose}>{t('fccs.cancel')}</Button>
          <Button
            type="primary"
            shape="round"
            className="btn-primary"
            disabled={!result}
            loading={applyMutation.isPending}
            onClick={() => applyMutation.mutate()}
          >
            {t('parentPl.importApply')}
          </Button>
        </Space>
      }
    >
      {!result && (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin />
        </div>
      )}
      {result && (
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
          <Text>
            {t('parentPl.importSummary', {
              year: result.year,
              sheet: result.sheetName,
              lines: ledgerCount,
              scale: result.scale === 1000 ? t('parentPl.inThousands') : '',
            })}
          </Text>
          {manual.length > 0 && (
            <Text>{t('parentPl.importManual', { list: manual.map((l) => l.label).join(', ') })}</Text>
          )}
          <Text>
            {result.mapping.fromFile
              ? t('parentPl.importMapFromFile', { pairs: result.mapping.pairs })
              : t('parentPl.importMapKept', { pairs: result.mapping.pairs })}
          </Text>
          {result.mapping.withoutLine.length > 0 && (
            <Alert
              type="warning"
              showIcon
              message={t('parentPl.importWithoutLine', {
                list: result.mapping.withoutLine.map((p) => `${p.account} ${p.orgUnit}`.trim()).join(', '),
              })}
            />
          )}
          {result.mapping.conflicts.length > 0 && (
            <Alert
              type="warning"
              showIcon
              message={t('parentPl.importConflicts', {
                list: result.mapping.conflicts
                  .map((c) => `${c.account} ${c.orgUnit}: ${c.lines.join(' / ')}`)
                  .join('; '),
              })}
            />
          )}
          <Text>
            {result.months.length
              ? t('parentPl.importMonths', {
                months: result.months.map((mo) => String(mo.month).padStart(2, '0')).join(', '),
                same: result.months.filter((mo) => !mo.differences.length).length,
              })
              : t('parentPl.importNoMonths')}
          </Text>
          {result.formYear !== result.year && (
            <Alert
              type="warning"
              showIcon
              message={t('parentPl.importOtherYear', { formYear: result.formYear, year: result.year })}
            />
          )}
          {result.unknownLines.length > 0 && (
            <Alert
              type="warning"
              showIcon
              message={t('parentPl.importUnknownLines', { list: result.unknownLines.join(', ') })}
            />
          )}
          {differences.length > 0 && (
            <Table
              size="small"
              pagination={false}
              scroll={{ y: 320 }}
              rowKey="key"
              dataSource={differences}
              columns={[
                {
                  title: t('parentPl.month'),
                  dataIndex: 'month',
                  key: 'month',
                  width: 80,
                  render: (m) => `${String(m).padStart(2, '0')}.${result.year}`,
                },
                { title: t('parentPl.line'), dataIndex: 'line', key: 'line' },
                {
                  title: t('fccs.inFile'),
                  dataIndex: 'inFile',
                  key: 'inFile',
                  align: 'right',
                  width: 140,
                  render: formatAmount,
                },
                {
                  title: t('parentPl.fromLedger'),
                  dataIndex: 'ledger',
                  key: 'ledger',
                  align: 'right',
                  width: 140,
                  render: formatAmount,
                },
              ]}
            />
          )}
          <Alert type="info" showIcon message={t('parentPl.importNote')} />
        </Space>
      )}
    </Modal>
  );
}
